import { segment } from '@/utils/segment'
import { EVENT_KEYS } from '@/utils/segment/segment-events'

interface ClickContext {
  hadSideEffect: boolean
}

interface VueInstance {
  type?: {
    __file?: string
  }
  $options?: {
    __file?: string
  }
  parent?: VueInstance
}

interface ElementWithVue extends HTMLElement {
  __vueParentComponent?: VueInstance
  __vue__?: VueInstance
}

const activeClicks = new Set<ClickContext>()
const vueFileCache = new WeakMap<HTMLElement, string | null>()
let isTrackerInitialized = false

let globalObserver: MutationObserver | null = null
let observerRefCount = 0

// Capture originals before any other code can patch them
const originalFetch = window.fetch
const originalXHROpen = XMLHttpRequest.prototype.open
const originalWindowOpen = window.open

// 16ms = 1 frame at 60 FPS (1000ms / 60 ≈ 16.67ms)
// Throttles side effect marking to match browser rendering cycle
// Reduces redundant processing when multiple mutations occur rapidly
const MARK_THROTTLE_MS = 16
const DETECTION_TIMEOUT_MS = 500

let lastMarkTime = 0

function markSideEffect() {
  const now = Date.now()

  if (now - lastMarkTime < MARK_THROTTLE_MS) return

  lastMarkTime = now
  activeClicks.forEach((ctx) => ctx.hadSideEffect = true)
}

function startGlobalObserver() {
  observerRefCount++

  if (!globalObserver) {
    globalObserver = new MutationObserver(() => markSideEffect())

    const appRoot = document.querySelector('#app') || document.body

    globalObserver.observe(appRoot, {
      childList: true,
      subtree: true,
    })
  }
}

function stopGlobalObserver() {
  observerRefCount--

  if (observerRefCount === 0 && globalObserver) {
    globalObserver.disconnect()
    globalObserver = null
  }
}

function getVueFile(el: HTMLElement): string | null {
  if (vueFileCache.has(el)) {
    return vueFileCache.get(el)!
  }

  const checkedNodes: HTMLElement[] = []
  const foundFiles: string[] = []
  let node: HTMLElement | null = el
  let depth = 0
  const MAX_DEPTH = 20

  while (node && depth < MAX_DEPTH) {
    if (vueFileCache.has(node)) {
      const cachedFile = vueFileCache.get(node)!

      checkedNodes.forEach((n) => vueFileCache.set(n, cachedFile))

      return cachedFile
    }

    checkedNodes.push(node)

    const elementWithVue = node as ElementWithVue
    let instance = elementWithVue.__vueParentComponent || elementWithVue.__vue__

    while (instance) {
      const file = instance?.type?.__file || instance?.$options?.__file

      if (file && !file.includes('node_modules')) foundFiles.push(file)

      instance = instance.parent
    }

    node = node.parentElement
    depth++
  }

  const result = foundFiles.sort((a, b) => {
    const depthA = a.split('/').length
    const depthB = b.split('/').length

    return depthB - depthA
  })[0] || null

  checkedNodes.forEach((n) => vueFileCache.set(n, result))

  return result
}

function shouldSkipLink(clickable: HTMLElement, event: MouseEvent): boolean {
  const anchor = clickable.tagName === 'A'
    ? (clickable as HTMLAnchorElement)
    : clickable.closest('a')

  if (!anchor) return false

  const targetAttr = anchor.getAttribute('target')

  return targetAttr === '_blank'
    || targetAttr === '_new'
    || (!!anchor.href && (event.ctrlKey || event.metaKey))
}

export function trackDeadClicks() {
  if (isTrackerInitialized) return

  isTrackerInitialized = true

  window.fetch = function (...args) {
    if (activeClicks.size > 0) markSideEffect()

    return originalFetch.apply(this, args)
  }

  XMLHttpRequest.prototype.open = function (
    method: string,
    url: string | URL,
    async: boolean = true,
    username?: string | null,
    password?: string | null,
  ) {
    if (activeClicks.size > 0) markSideEffect()

    return originalXHROpen.call(this, method, url, async, username, password)
  }

  window.open = function (...args) {
    if (activeClicks.size > 0) markSideEffect()

    return originalWindowOpen.apply(this, args)
  }

  document.addEventListener('click', (event: MouseEvent) => {
    const target = event.target as HTMLElement
    const clickable = target.closest<HTMLElement>('button, [role="button"], a')

    if (!clickable || shouldSkipLink(clickable, event)) return

    const initialUrl = location.href
    const context: ClickContext = { hadSideEffect: false }

    activeClicks.add(context)
    startGlobalObserver()

    setTimeout(() => {
      stopGlobalObserver()
      activeClicks.delete(context)

      if (context.hadSideEffect || location.href !== initialUrl) return

      try {
        const styles = window.getComputedStyle(clickable)

        if (styles.cursor !== 'pointer') return

        segment.trackEventWithUser(EVENT_KEYS.DEAD_CLICK, {
          buttonText: clickable.textContent?.trim() || null,
          component: getVueFile(clickable),
        })
      } catch (error) {
        console.error('[Dead Click Tracker] Error tracking dead click:', error)
      }
    }, DETECTION_TIMEOUT_MS)
  }, true)
}

export function cleanupDeadClickTracker() {
  globalObserver?.disconnect()
  globalObserver = null
  observerRefCount = 0
  activeClicks.clear()
  isTrackerInitialized = false
}
